import { useRef, useState } from 'react'
import {
  getOpticalMeasureDistanceRange,
  type FaceData,
  type OpticalMeasureFramingDistanceProfileSettings,
  type OpticalMeasureSHCaptureSettings,
  type OpticalMeasureSHGateSettings,
  type SHCaptureData,
} from '../../index'
import type { MeasurementFlowPhase } from '../types'
import { deriveGuidancePhase } from './deriveGuidancePhase'

export function useSHCapture(
  capture: OpticalMeasureSHCaptureSettings,
  distanceProfile: OpticalMeasureFramingDistanceProfileSettings,
  gates: OpticalMeasureSHGateSettings
) {
  const targetCandidateFrames = Math.max(
    1,
    Math.round(capture.targetCandidateFrames)
  )
  const [phase, setPhase] = useState<MeasurementFlowPhase>('idle')
  const [capturedCount, setCapturedCount] = useState(0)
  const [targetCount, setTargetCount] = useState(targetCandidateFrames)
  const [nativeState, setNativeState] = useState<SHCaptureData | null>(null)

  const activeRef = useRef(false)
  const nativeSessionIdRef = useRef<number | null>(null)
  const captureRef = useRef(capture)
  const distanceProfileRef = useRef(distanceProfile)
  const gatesRef = useRef(gates)

  const start = () => {
    captureRef.current = capture
    distanceProfileRef.current = distanceProfile
    gatesRef.current = gates
    activeRef.current = true
    nativeSessionIdRef.current = null
    setCapturedCount(0)
    setTargetCount(targetCandidateFrames)
    setNativeState(null)
    setPhase('findingFrame')
  }

  const stop = () => {
    activeRef.current = false
    nativeSessionIdRef.current = null
    setPhase('idle')
  }

  const clear = () => {
    activeRef.current = false
    nativeSessionIdRef.current = null
    setCapturedCount(0)
    setTargetCount(targetCandidateFrames)
    setNativeState(null)
    setPhase('idle')
  }

  const phaseForNativeRejection = (
    reason: string | undefined,
    incoming?: FaceData
  ): MeasurementFlowPhase | null => {
    // Only the classifier's "no glasses on this face" verdict prompts the
    // wearer; `glasses-presence-unavailable` (model load/inference failure) is
    // not their problem, so it falls through to the generic `findingFrame`.
    if (reason === 'glasses-not-detected') return 'noGlasses'
    if (reason === 'eyes-closed') return 'opening'
    if (reason === 'pose-out-of-range') return 'positioning'
    if (reason === 'distance-out-of-range') {
      if (!incoming) return 'positioning'
      return incoming.distanceCm <
        distanceProfileRef.current.preferredDistanceCm
        ? 'tooClose'
        : 'tooFar'
    }
    return null
  }

  const mapNativePhase = (
    state: SHCaptureData,
    incoming?: FaceData
  ): MeasurementFlowPhase => {
    if (state.phase === 'idle') return 'idle'
    if (state.phase === 'calibrating') return 'calibrating'
    if (state.phase === 'complete') return 'complete'
    if (state.phase === 'failed') return 'failed'

    // Guidance stays live through the hold. Sampling used to short-circuit
    // straight to `sampling`, which left the subject with no feedback for the
    // only stretch that decides what distance and pose the capture actually
    // happens at — so they settled wherever the prompt had last gone quiet,
    // which is the near edge of whatever band, and native accepted it because
    // its own bands are wider still. Progress is `capturedCount / targetCount`
    // and independent of phase, so the bar holds its place while the message
    // asks them to move.
    //
    // Judged against the *native* gates here, not the tighter guidance
    // constants: mid-capture the message is explaining why frames are or are
    // not being taken, so nagging about a pose native is happily accepting
    // would contradict the advancing progress bar. Distance is the exception —
    // it stays on the guidance band, because drifting to the edge of the
    // capture band is exactly the failure this whole change exists to stop.
    if (state.phase === 'capturing') {
      if (!incoming) return 'sampling'
      return (
        deriveGuidancePhase(incoming, {
          distanceRange: getOpticalMeasureDistanceRange(
            distanceProfileRef.current,
            'guidance'
          ),
          eyeOpenThreshold: gatesRef.current.eyeOpenMin,
          rotationRange: {
            xDeg: gatesRef.current.maxRotationXDeg,
            yDeg: gatesRef.current.maxRotationYDeg,
          },
        }) ?? 'sampling'
      )
    }

    if (!incoming) return 'detecting'
    const guidancePhase = deriveGuidancePhase(incoming, {
      distanceRange: getOpticalMeasureDistanceRange(
        distanceProfileRef.current,
        'guidance'
      ),
      eyeOpenThreshold: captureRef.current.eyeOpenThreshold,
      // The native gate, not `ROTATION_X_THRESHOLD`. That constant is 5, which
      // was tighter than the old gate of 8 and so guided *into* what native
      // accepts — but the gate is 3 now, which inverts it: guidance would go
      // quiet at ±4 while every frame there is silently rejected, leaving the
      // subject with a pose complaint that never appears and a capture that
      // never starts. Guidance must never be looser than the gate.
      rotationRange: {
        xDeg: gatesRef.current.maxRotationXDeg,
        yDeg: gatesRef.current.maxRotationYDeg,
      },
    })
    return (
      guidancePhase ??
      phaseForNativeRejection(state.rejectionReason, incoming) ??
      'findingFrame'
    )
  }

  const applyCurrentNativeState = (
    state: SHCaptureData | undefined,
    incoming?: FaceData
  ) => {
    if (!state) return

    activeRef.current = state.active
    setNativeState(state)
    setCapturedCount(state.capturedCount)
    setTargetCount(Math.max(1, state.targetCount))
    setPhase(mapNativePhase(state, incoming))
  }

  // Only command responses establish capture identity. Face-data events are
  // asynchronous and may still be queued from a capture that was just retried.
  const applyNativeState = (
    state: SHCaptureData | undefined,
    incoming?: FaceData
  ) => {
    if (!state) return
    nativeSessionIdRef.current = state.sessionId
    applyCurrentNativeState(state, incoming)
  }

  const handleFaceData = (incoming: FaceData) => {
    if (incoming.shCapture) {
      if (incoming.shCapture.sessionId !== nativeSessionIdRef.current) return
      applyCurrentNativeState(incoming.shCapture, incoming)
      return
    }

    if (!activeRef.current) return

    if (incoming.calibrating) {
      setPhase('calibrating')
      return
    }

    const guidancePhase = deriveGuidancePhase(incoming, {
      distanceRange: getOpticalMeasureDistanceRange(
        distanceProfileRef.current,
        'guidance'
      ),
      eyeOpenThreshold: captureRef.current.eyeOpenThreshold,
      // The native gate, not `ROTATION_X_THRESHOLD`. That constant is 5, which
      // was tighter than the old gate of 8 and so guided *into* what native
      // accepts — but the gate is 3 now, which inverts it: guidance would go
      // quiet at ±4 while every frame there is silently rejected, leaving the
      // subject with a pose complaint that never appears and a capture that
      // never starts. Guidance must never be looser than the gate.
      rotationRange: {
        xDeg: gatesRef.current.maxRotationXDeg,
        yDeg: gatesRef.current.maxRotationYDeg,
      },
    })
    setPhase(guidancePhase ?? 'findingFrame')
  }

  const getNativeSessionId = () => nativeSessionIdRef.current

  return {
    phase,
    capturedCount,
    targetCount,
    progress: capturedCount / targetCount,
    nativeState,
    start,
    stop,
    clear,
    applyNativeState,
    handleFaceData,
    getNativeSessionId,
  }
}
